import { useState, useEffect, useRef, useCallback, useMemo } from "react";

import { useReactToPrint } from 'react-to-print';

import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";

import { RelacaoTecido } from "../../components/Outros/RelacaoTecido";

import Loading from "../../components/Loading";
import Menu from "../../components/Menu";

import api from "../../services/api";

import ComposicaoArtigoDialog from "./ComposicaoArtigoDialog";
import ConfiancaDialog from "./ConfiancaDialog";
import ConsumoFio from "./ConsumoFio";
import DecisaoAbastecimento from "./DecisaoAbastecimento";
import HorarioPrevisao from "./HorarioPrevisao";
import ImportacaoDialog from "./ImportacaoDialog";
import GraficoSaidaTecido from "../../components/Graficos/PrevisaoDeDemanda";
import PCP from "./PCP";
import PlanejamentoRoladas from "./PlanejamentoRoladas";
import ProgramacaoTeares from "./ProgramacaoTeares";
import RelatorioAccordion, { RELATORIOS_PROGRAMACAO } from "./RelatorioAccordion";
import {
    PRIORIDADE_ACAO,
    calcularPrevisoesOperacionais,
    criarLinhaRolada,
} from "./shared";

const STORAGE_KEY_PROGRAMACAO_TEARES_COMPLETA = 'programacao_teares_exibir_programacao_completa';

export default function ProgramacaoRolo() {
    const [artigos, setArtigos] = useState([]);
    const [data, setData] = useState([]);
    const [consumo, setConsumo] = useState([]);
    const [abastecimento, setAbastecimento] = useState({
        resumo: {},
        artigos: [],
        recomendacoes: [],
        metodologia: {},
    });
    const [roladasEsboco, setRoladasEsboco] = useState([]);
    const [exibirRoladasInativas, setExibirRoladasInativas] = useState(false);
    const [values, setValues] = useState({
        artigos: [],
        datas: [],
        titulo: '',
        cores: {},
        turmas: [],
    });
    const [turmas, setTurmas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [printing, setPrinting] = useState(false);
    const [visible, setVisible] = useState(false);
    const [detalhePcp, setDetalhePcp] = useState(null);
    const [detalheConfianca, setDetalheConfianca] = useState(null);
    const [exibirProgramacaoCompletaTeares, setExibirProgramacaoCompletaTeares] = useState(() => (
        typeof window !== 'undefined'
            && window.localStorage.getItem(STORAGE_KEY_PROGRAMACAO_TEARES_COMPLETA) === 'true'
    ));

    const toast = useRef();
    const ref = useRef();
    const prog = useRef();
    const planilhaRoladas = useRef();
    const roladasEsbocoAtuais = useRef([]);
    const salvamentosRolada = useRef(new Map());

    const showError = (error) => {
        toast.current?.show({
            summary: 'Ocorreu um Erro',
            severity: 'error',
            detail: error.response ? error.response.data.message : error.message
        });
    }

    const showSuccess = (response) => {
        toast.current?.show({
            summary: 'Sucesso',
            severity: 'success',
            detail: response.data.message
        });
    }

    const fetchConsumoFio = useCallback(() => {
        api.get("consumo-fio").then((response) => {
            setConsumo(response.data);
        }).catch((error) => {
            showError(error);
        });
    }, []);

    const getData = useCallback((atualizarMetricas = false, exibirLoading = true) => {
        if (exibirLoading) {
            setLoading(true);
        }

        api.get('programacao-fim-rolo', {
            params: atualizarMetricas ? { atualizar_metricas: 1 } : {}
        }).then((response) => {
            setData(response.data.programacao);
            setValues(response.data.values);
            setAbastecimento(response.data.abastecimento || {
                resumo: {},
                artigos: [],
                recomendacoes: [],
                metodologia: {},
            });
            if (exibirLoading) {
                setLoading(false);
            }
        }).catch((error) => {
            if (exibirLoading) {
                setLoading(false);
            }
            showError(error);
        });
    }, []);

    const handleUpload = (e) => {
        const file = e.files[0];
        const formData = new FormData();
        formData.append('programacao', file);

        setLoading(true);
        api.post('programacao-fim-rolo/store', formData, {
            headers: { 'Content-Type': `multipart/form-data` }
        }).then((response) => {
            getData();
            setLoading(false);
            showSuccess(response);
        }).catch((error) => {
            setLoading(false);
            showError(error);
        });
    }

    const fetchRoladasEsboco = useCallback(() => {
        api.get('programacao-fim-rolo/roladas-esboco').then((response) => {
            const linhas = response.data.roladas.map((rolada) => criarLinhaRolada(rolada));
            const linhasComNovaRolada = [...linhas, criarLinhaRolada()];

            roladasEsbocoAtuais.current = linhasComNovaRolada;
            setRoladasEsboco(linhasComNovaRolada);
        }).catch((error) => {
            showError(error);
        });
    }, []);

    const atualizarCampoRolada = (chave, campo, valor) => {
        const linhasAtualizadas = roladasEsbocoAtuais.current.map((linha) => {
            if (linha.chave !== chave) {
                return linha;
            }

            return {
                ...linha,
                [campo]: valor,
                alterada: true,
                erro: '',
            };
        });

        roladasEsbocoAtuais.current = linhasAtualizadas;
        setRoladasEsboco(linhasAtualizadas);
    }

    const linhaRoladaPreenchida = (linha) => {
        return linha.numero_rolada
            || linha.cod_artigo
            || linha.metragem_total
            || linha.data_inicio_planejada
            || linha.data_disponibilidade_planejada;
    }

    const validarLinhaRolada = (linha) => {
        if (!linha.numero_rolada) {
            return 'Informe o número da rolada.';
        }
        if (!/^\d{2}-\d{3}$/.test(linha.numero_rolada)) {
            return 'O número da rolada deve seguir o formato 99-999.';
        }
        if (!linha.cod_artigo || Number(linha.cod_artigo) <= 0) {
            return 'Informe um código de artigo válido.';
        }
        if (!linha.metragem_total || Number(linha.metragem_total) <= 0) {
            return 'A metragem total deve ser maior que zero.';
        }
        if (
            linha.data_inicio_planejada
            && linha.data_disponibilidade_planejada
            && linha.data_disponibilidade_planejada < linha.data_inicio_planejada
        ) {
            return 'A disponibilidade não pode ser anterior ao início da rolada.';
        }

        return '';
    }

    const marcarErroLinha = (chave, erro) => {
        setRoladasEsboco((linhas) => linhas.map((linha) => (
            linha.chave === chave ? { ...linha, salvando: false, erro } : linha
        )));
    }

    const salvarLinhaRolada = (linha, exibirMensagem = true) => {
        if (salvamentosRolada.current.has(linha.chave)) {
            return salvamentosRolada.current.get(linha.chave);
        }

        const erro = validarLinhaRolada(linha);

        if (erro) {
            marcarErroLinha(linha.chave, erro);
            return Promise.reject(new Error(erro));
        }

        const dados = {
            numero_rolada: linha.numero_rolada,
            cod_artigo: Number(linha.cod_artigo),
            metragem_total: Number(linha.metragem_total),
            data_inicio_planejada: linha.data_inicio_planejada || null,
            data_disponibilidade_planejada: linha.data_disponibilidade_planejada || null,
            ativo: linha.ativo,
        };
        const request = linha.id
            ? api.put(`programacao-fim-rolo/roladas-esboco/${linha.id}`, dados)
            : api.post('programacao-fim-rolo/roladas-esboco', dados);

        setRoladasEsboco((linhas) => linhas.map((item) => (
            item.chave === linha.chave ? { ...item, salvando: true, erro: '' } : item
        )));

        const salvamento = request.then((response) => {
            const linhaSalva = {
                ...criarLinhaRolada(response.data.rolada),
                salvaRecentemente: true,
            };

            setRoladasEsboco((linhas) => {
                const atualizadas = linhas.map((item) => (
                    item.chave === linha.chave ? linhaSalva : item
                ));
                const possuiLinhaVazia = atualizadas.some((item) => !item.id && !linhaRoladaPreenchida(item));

                return possuiLinhaVazia ? atualizadas : [...atualizadas, criarLinhaRolada()];
            });

            if (exibirMensagem) {
                showSuccess(response);
                getData(false, false);
            }

            setTimeout(() => {
                setRoladasEsboco((linhas) => linhas.map((item) => (
                    item.chave === linhaSalva.chave
                        ? { ...item, salvaRecentemente: false }
                        : item
                )));
            }, 2000);

            return response;
        }).catch((error) => {
            const mensagem = error.response?.data?.message || error.message;
            marcarErroLinha(linha.chave, mensagem);

            if (error.response && exibirMensagem) {
                showError(error);
            }

            return Promise.reject(error);
        }).finally(() => {
            salvamentosRolada.current.delete(linha.chave);
        });

        salvamentosRolada.current.set(linha.chave, salvamento);

        return salvamento;
    }

    const adicionarLinhaRolada = (focar = true) => {
        const novaLinha = criarLinhaRolada();

        setRoladasEsboco((linhas) => [...linhas, novaLinha]);

        if (focar) {
            setTimeout(() => {
                planilhaRoladas.current
                    ?.querySelector(`[data-chave="${novaLinha.chave}"][data-coluna="0"]`)
                    ?.focus();
            }, 0);
        }
    }

    const focarCelulaRolada = (linhaIndex, colunaIndex) => {
        setTimeout(() => {
            planilhaRoladas.current
                ?.querySelector(`[data-linha="${linhaIndex}"][data-coluna="${colunaIndex}"]`)
                ?.focus();
        }, 0);
    }

    const handleBlurRolada = (event, linha, linhaIndex) => {
        const proximoElemento = event.relatedTarget;
        const permaneceuNaLinha = proximoElemento?.dataset?.linha === String(linhaIndex);

        if (permaneceuNaLinha) {
            return;
        }

        setTimeout(() => {
            const linhaAtual = roladasEsbocoAtuais.current.find((item) => item.chave === linha.chave);

            if (
                !linhaAtual
                || !linhaAtual.alterada
                || linhaAtual.salvando
                || salvamentosRolada.current.has(linhaAtual.chave)
                || !linhaRoladaPreenchida(linhaAtual)
            ) {
                return;
            }

            salvarLinhaRolada(linhaAtual, false)
                .then(() => getData(false, false))
                .catch(showError);
        }, 0);
    }

    const handleTeclaRolada = (event, linha, linhaIndex, colunaIndex) => {
        const totalColunas = 6;

        if (event.key !== 'Tab') {
            return;
        }

        event.preventDefault();
        let proximaLinha = linhaIndex;
        let proximaColuna = colunaIndex + (event.shiftKey ? -1 : 1);

        if (proximaColuna >= totalColunas) {
            proximaLinha += 1;
            proximaColuna = 0;

            if (linha.alterada && linhaRoladaPreenchida(linha)) {
                salvarLinhaRolada(linha, false)
                    .then(() => getData(false, false))
                    .catch(() => {});
            }
        } else if (proximaColuna < 0) {
            proximaLinha -= 1;
            proximaColuna = totalColunas - 1;
        }

        if (proximaLinha >= roladasVisiveis.length) {
            adicionarLinhaRolada(false);
        }

        if (proximaLinha >= 0) {
            focarCelulaRolada(proximaLinha, proximaColuna);
        }
    }

    const handlePrint = useReactToPrint({
        content: () => prog.current,
        onBeforeGetContent: () => {
            return new Promise((resolve) => {
                setPrinting(true);
                setTimeout(() => {
                    resolve();
                }, 500);
            });
        },
        onAfterPrint: () => {
            setPrinting(false);
        },
        pageStyle: `
        @media print {
            #my-table {
                transform: rotate(90deg);
            },
        }
    `
    })

    const handlePrintTable = useReactToPrint({
        content: () => ref.current,
        onBeforeGetContent: () => {
            return new Promise((resolve) => {
                setPrinting(true);
                setTimeout(() => {
                    resolve();
                }, 500);
            });
        },
        onAfterPrint: () => {
            setPrinting(false);
        },
    });

    useEffect(() => {
        getData();
        fetchConsumoFio();
        fetchRoladasEsboco();
    }, [getData, fetchConsumoFio, fetchRoladasEsboco]);
    useEffect(() => {
        roladasEsbocoAtuais.current = roladasEsboco;
    }, [roladasEsboco]);
    useEffect(() => {
        window.localStorage.setItem(
            STORAGE_KEY_PROGRAMACAO_TEARES_COMPLETA,
            exibirProgramacaoCompletaTeares ? 'true' : 'false'
        );
    }, [exibirProgramacaoCompletaTeares]);

    const programacaoAtualTeares = useMemo(() => {
        return data.filter((item) => Number(item.numrolo) === 1);
    }, [data]);
    const programacaoVisivelTeares = exibirProgramacaoCompletaTeares
        ? data
        : programacaoAtualTeares;
    const valuesProgramacaoTeares = useMemo(() => {
        const artigosAtuais = programacaoVisivelTeares
            .map((item) => item.artigo)
            .filter((artigo, index, artigosLista) => artigosLista.indexOf(artigo) === index);
        const datasAtuais = values.datas.filter((dataItem) => (
            programacaoVisivelTeares.some((item) => item.dataf === dataItem)
        ));

        return {
            ...values,
            artigos: values.artigos.filter((artigo) => artigosAtuais.includes(artigo)),
            datas: datasAtuais,
        };
    }, [programacaoVisivelTeares, values]);
    const programacaoPorArtigoData = useMemo(() => {
        return programacaoVisivelTeares.reduce((agrupado, item) => {
            const chave = `${item.artigo}|${item.dataf}`;

            if (!agrupado[chave]) {
                agrupado[chave] = [];
            }

            agrupado[chave].push(item);

            return agrupado;
        }, {});
    }, [programacaoVisivelTeares]);
    const totaisPorArtigo = useMemo(() => {
        return programacaoVisivelTeares.reduce((totais, item) => {
            totais[item.artigo] = (totais[item.artigo] || 0) + 1;

            return totais;
        }, {});
    }, [programacaoVisivelTeares]);

    if (loading) {
        return (
            <>
                <Toast ref={toast} />
                <Loading />
            </>
        )
    }

    const filteredArtigos = valuesProgramacaoTeares.artigos.filter((element) => {
        return !artigos.includes(element);
    });
    const quantidadeRoladasInativas = roladasEsboco.filter((linha) => linha.id && !linha.ativo).length;
    const roladasVisiveis = roladasEsboco.filter((linha) => (
        exibirRoladasInativas
        || linha.ativo
        || !linha.id
        || linha.alterada
        || linha.salvando
        || linha.salvaRecentemente
    ));

    const previsoesOperacionais = calcularPrevisoesOperacionais(abastecimento);
    const acoesOperacionais = abastecimento.recomendacoes.map((recomendacao) => {
        const previsao = previsoesOperacionais.find((item) => (
            item.cod_artigo === recomendacao.cod_artigo
        ));
        const prioridade = PRIORIDADE_ACAO[recomendacao.tipo] || 3;

        return { ...recomendacao, previsao, prioridade };
    }).sort((primeira, segunda) => primeira.prioridade - segunda.prioridade);
    const indicadoresAbastecimento = {
        criticos: previsoesOperacionais.filter((item) => item.situacao === 'critico').length,
        cobertosPeloPlano: previsoesOperacionais.filter((item) => (
            item.cenario_conservador.risco === 'critico' && item.cenario_potencial.risco !== 'critico'
        )).length,
        estaveis: previsoesOperacionais.filter((item) => item.situacao === 'normal').length,
        semHistorico: previsoesOperacionais.filter((item) => item.confianca === 'sem_dados').length,
    };
    const memoriaDetalhe = detalhePcp?.memoria_calculo;
    const criterioConfiancaDetalhe = detalhePcp
        ? abastecimento.metodologia?.classificacao_confianca?.[detalhePcp.confianca]
        : null;
    const horizontePcpDias = abastecimento.metodologia?.horizonte_pcp_dias;
    const textoHorizontePcp = horizontePcpDias
        ? `próximos ${horizontePcpDias} dias`
        : 'período configurado no PCP';

    const alternarArtigo = (artigo) => {
        setArtigos((artigosAtuais) => (
            artigosAtuais.includes(artigo)
                ? artigosAtuais.filter((item) => item !== artigo)
                : [...artigosAtuais, artigo]
        ));
    };
    const alternarTurma = (turma) => {
        setTurmas((turmasAtuais) => (
            turmasAtuais.includes(turma)
                ? turmasAtuais.filter((item) => item !== turma)
                : [...turmasAtuais, turma]
        ));
    };

    return (
        <div>
            <Menu />
            <Toast ref={toast} />

            <ImportacaoDialog
                onHide={() => setVisible(false)}
                onUpload={handleUpload}
                visible={visible}
            />
            <ComposicaoArtigoDialog
                criterioConfianca={criterioConfiancaDetalhe}
                detalhe={detalhePcp}
                memoria={memoriaDetalhe}
                onAbrirConfianca={setDetalheConfianca}
                onHide={() => setDetalhePcp(null)}
            />
            <ConfiancaDialog
                detalhe={detalheConfianca}
                metodologia={abastecimento.metodologia}
                onHide={() => setDetalheConfianca(null)}
            />

            <div className="p-d-flex p-flex-row p-jc-end p-m-1">
                <Button
                    className="p-m-1"
                    icon="pi pi-refresh"
                    label="Atualizar"
                    onClick={() => setVisible(true)}
                />
            </div>
            <Card
                className="p-m-1"
                title={values.titulo}
            >
                <RelatorioAccordion relatorio={RELATORIOS_PROGRAMACAO.PCP} titulo="PCP">
                    <PCP />
                </RelatorioAccordion>

                <RelatorioAccordion
                    relatorio={RELATORIOS_PROGRAMACAO.PROGRAMACAO_TEARES}
                    titulo="PROGRAMAÇÃO DOS TEARES"
                >
                    <ProgramacaoTeares
                        artigosOcultos={artigos}
                        filteredArtigos={filteredArtigos}
                        onPrint={handlePrint}
                        onToggleProgramacaoCompleta={setExibirProgramacaoCompletaTeares}
                        onToggleArtigo={alternarArtigo}
                        onToggleTurma={alternarTurma}
                        printing={printing}
                        programacaoPorArtigoData={programacaoPorArtigoData}
                        progRef={prog}
                        totaisPorArtigo={totaisPorArtigo}
                        turmasOcultas={turmas}
                        values={valuesProgramacaoTeares}
                        visualizacaoCompleta={exibirProgramacaoCompletaTeares}
                    />
                </RelatorioAccordion>

                <RelatorioAccordion
                    relatorio={RELATORIOS_PROGRAMACAO.PLANEJAMENTO_ROLADAS}
                    titulo="PLANEJAMENTO DE ROLADAS"
                >
                    <PlanejamentoRoladas
                        exibirInativas={exibirRoladasInativas}
                        onAdicionarLinha={adicionarLinhaRolada}
                        onAtualizarCampo={atualizarCampoRolada}
                        onBlurLinha={handleBlurRolada}
                        onChangeExibirInativas={setExibirRoladasInativas}
                        onTecla={handleTeclaRolada}
                        planilhaRef={planilhaRoladas}
                        quantidadeInativas={quantidadeRoladasInativas}
                        roladas={roladasVisiveis}
                    />
                </RelatorioAccordion>

                <RelatorioAccordion
                    relatorio={RELATORIOS_PROGRAMACAO.DECISAO_ABASTECIMENTO}
                    titulo="DECISÃO DE ABASTECIMENTO"
                >
                    <DecisaoAbastecimento
                        acoes={acoesOperacionais}
                        indicadores={indicadoresAbastecimento}
                        onAbrirComposicao={setDetalhePcp}
                        onAbrirConfianca={setDetalheConfianca}
                        onAtualizarHistorico={() => getData(true)}
                        previsoes={previsoesOperacionais}
                        textoHorizontePcp={textoHorizontePcp}
                    />
                </RelatorioAccordion>

                <RelatorioAccordion
                    relatorio={RELATORIOS_PROGRAMACAO.RELACAO_TECIDO}
                    titulo="RELAÇÃO DE TECIDO"
                >
                    <RelacaoTecido data={data} values={values} />
                </RelatorioAccordion>

                <RelatorioAccordion
                    inicialmenteAberto={false}
                    relatorio={RELATORIOS_PROGRAMACAO.HORARIO_PREVISAO}
                    titulo="HORÁRIO DE PREVISÃO"
                >
                    <HorarioPrevisao
                        data={data}
                        onPrint={handlePrintTable}
                        tableRef={ref}
                    />
                </RelatorioAccordion>

                <RelatorioAccordion
                    relatorio={RELATORIOS_PROGRAMACAO.GRAFICO_SAIDA_TECIDO}
                    titulo="PREVISÃO DE DEMANDA"
                >
                    <GraficoSaidaTecido />
                </RelatorioAccordion>

                <RelatorioAccordion
                    inicialmenteAberto={false}
                    relatorio={RELATORIOS_PROGRAMACAO.CONSUMO_FIO}
                    titulo="CONSUMO DE FIO DO DIA"
                >
                    <ConsumoFio consumo={consumo} />
                </RelatorioAccordion>
            </Card>
        </div>
    )
}
